import React, { useMemo, useState } from 'react';
import { Card, Input, Row, Col, Select, Tag, Typography } from 'antd';
import { SearchOutlined, ApiOutlined } from '@ant-design/icons';
import StandardTable from '../../components/common/StandardTable';

const { Title, Text } = Typography;
const { Option } = Select;

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface ApiEndpoint {
  id: number;
  category: string;
  name: string;
  methods: HttpMethod[];
  path: string;
  description: string;
  dbTables: string;
  frontendPages: string[];
}

// NOT: Tüm path'ler /api blueprint prefix'i ile birlikte düşünülmelidir.
const apiEndpoints: ApiEndpoint[] = [
  // AUTH
  {
    id: 1,
    category: 'Auth',
    name: 'Kullanıcı Girişi (Login)',
    methods: ['POST'],
    path: '/api/auth/login',
    description: 'Kullanıcı adı ve şifre ile giriş yapar, JWT token döner.',
    dbTables: 'it_users, it_logs_user_logins',
    frontendPages: ['/login'],
  },
  {
    id: 2,
    category: 'Auth',
    name: 'Oturum Bilgisi (Me)',
    methods: ['GET'],
    path: '/api/auth/me',
    description: 'Token içindeki kullanıcı bilgilerini döner.',
    dbTables: 'it_users',
    frontendPages: ['AuthContext / global'],
  },
  {
    id: 3,
    category: 'Auth',
    name: 'Çıkış (Logout)',
    methods: ['POST'],
    path: '/api/auth/logout',
    description: 'Aktif oturumu sonlandırır ve login logunu günceller.',
    dbTables: 'it_logs_user_logins',
    frontendPages: ['AuthContext / global'],
  },

  // ÇALIŞMA İZİNLERİ
  {
    id: 10,
    category: 'Çalışma İzinleri',
    name: 'Çalışma İzni Oluştur',
    methods: ['POST'],
    path: '/api/work_permits',
    description: 'Yeni çalışma izni kaydı oluşturur.',
    dbTables: 'work_permits, work_permit_field_personnel, gas_tests vb.',
    frontendPages: ['/work-permits/create'],
  },
  {
    id: 11,
    category: 'Çalışma İzinleri',
    name: 'Çalışma İzni Güncelle',
    methods: ['PUT'],
    path: '/api/work_permits/<permit_id>',
    description: 'Var olan çalışma iznini günceller.',
    dbTables: 'work_permits, work_permit_field_personnel',
    frontendPages: ['/work-permits/edit/:id'],
  },
  {
    id: 12,
    category: 'Çalışma İzinleri',
    name: 'Sahada Çalışacak Personel Ekle',
    methods: ['POST'],
    path: '/api/work_permits/<permit_id>/field_personnel',
    description: 'İzne sahada çalışacak personel ekler.',
    dbTables: 'work_permit_field_personnel',
    frontendPages: ['/work-permits/edit/:id'],
  },
  {
    id: 13,
    category: 'Çalışma İzinleri',
    name: 'Saha Personeli Sil',
    methods: ['DELETE'],
    path: '/api/work_permits/field_personnel/<field_personnel_id>',
    description: 'Çalışma izninden saha personeli kaydını siler.',
    dbTables: 'work_permit_field_personnel',
    frontendPages: ['/work-permits/edit/:id'],
  },

  // PostgreSQL dbExchange
  {
    id: 20,
    category: 'PostgreSQL dbExchange',
    name: 'Tablo Verisi Getir',
    methods: ['GET'],
    path: '/api/dbExchange/get',
    description: 'Belirtilen PostgreSQL tablosundan satırları okur (opsiyonel filtre ile).',
    dbTables: 'dinamik (table_name parametresi)',
    frontendPages: ['Generic list for many modules'],
  },
  {
    id: 21,
    category: 'PostgreSQL dbExchange',
    name: 'Upsert',
    methods: ['POST'],
    path: '/api/dbExchange/upsert',
    description: 'Var olan kaydı günceller veya yeni kayıt ekler (JSON payload ile).',
    dbTables: 'dinamik (table_name parametresi)',
    frontendPages: ['Generic forms / gridler'],
  },
  {
    id: 22,
    category: 'PostgreSQL dbExchange',
    name: 'Batch Update',
    methods: ['POST'],
    path: '/api/dbExchange/update_batch',
    description: 'Birden fazla kaydı toplu günceller.',
    dbTables: 'dinamik (table_name parametresi)',
    frontendPages: ['Bulk edit ekranları'],
  },
  {
    id: 23,
    category: 'PostgreSQL dbExchange',
    name: 'Raw Query',
    methods: ['GET', 'POST'],
    path: '/api/dbExchange/getquery',
    description: 'Raw SQL SELECT sorgularını çalıştırır (ALLOW_RAW_SQL=true ise).',
    dbTables: 'dinamik (sorguya bağlı)',
    frontendPages: ['Gelişmiş raporlar / admin'],
  },
  {
    id: 24,
    category: 'PostgreSQL dbExchange',
    name: 'Tarih Aralığı Sorgu',
    methods: ['GET'],
    path: '/api/dbExchange/get_between_dates',
    description: 'Belirli tarih aralığında satırları getirir.',
    dbTables: 'dinamik (table_name parametresi)',
    frontendPages: ['Raporlama ekranları'],
  },
  {
    id: 25,
    category: 'PostgreSQL dbExchange',
    name: 'Insert',
    methods: ['POST'],
    path: '/api/dbExchange/insert',
    description: 'Tek veya çoklu insert ile PostgreSQL tablosuna kayıt ekler.',
    dbTables: 'dinamik (table_name parametresi)',
    frontendPages: ['Çeşitli create ekranları'],
  },
  {
    id: 26,
    category: 'PostgreSQL dbExchange',
    name: 'Insert Header + Items',
    methods: ['POST'],
    path: '/api/dbExchange/insert_header_with_items',
    description: 'Header ve detay satırlarını tek transaction içinde ekler.',
    dbTables: 'header + items tabloları',
    frontendPages: ['Master-detail formlar'],
  },
  {
    id: 27,
    category: 'PostgreSQL dbExchange',
    name: 'Multipart Insert',
    methods: ['POST'],
    path: '/api/dbExchange/multipart_insert',
    description: 'Form-data ile dosyadan/çok parçalı veri insert eder.',
    dbTables: 'dinamik',
    frontendPages: ['CSV/Excel import benzeri senaryolar'],
  },
  {
    id: 28,
    category: 'PostgreSQL dbExchange',
    name: 'Update Data',
    methods: ['PUT'],
    path: '/api/dbExchange/update_data',
    description: 'Belirli koşula göre tek kaydı günceller.',
    dbTables: 'dinamik',
    frontendPages: ['Çeşitli edit ekranları'],
  },
  {
    id: 29,
    category: 'PostgreSQL dbExchange',
    name: 'Delete',
    methods: ['DELETE'],
    path: '/api/dbExchange/delete',
    description: 'Filtreye göre satır(ları) siler.',
    dbTables: 'dinamik',
    frontendPages: ['Silme işlemleri (genel)'],
  },
  {
    id: 30,
    category: 'PostgreSQL dbExchange',
    name: 'Ping',
    methods: ['GET'],
    path: '/api/dbExchange/ping',
    description: 'PostgreSQL bağlantısını test eder.',
    dbTables: '-',
    frontendPages: ['/core/cache', 'health checks'],
  },
  {
    id: 31,
    category: 'PostgreSQL dbExchange',
    name: 'Stream Table NDJSON',
    methods: ['GET'],
    path: '/api/dbExchange/stream',
    description: 'Büyük tabloları satır satır NDJSON formatında stream eder.',
    dbTables: 'dinamik',
    frontendPages: ['Büyük veri içeren raporlar'],
  },
  {
    id: 32,
    category: 'PostgreSQL dbExchange',
    name: 'Labor Types',
    methods: ['GET'],
    path: '/api/api/labor_types',
    description: 'Meslek tanımlarından distinct labor_type listesini döner.',
    dbTables: 'cor_professions',
    frontendPages: ['/core/general/professions/list'],
  },
  {
    id: 33,
    category: 'PostgreSQL dbExchange',
    name: 'Exec DDL (View Oluştur)',
    methods: ['POST'],
    path: '/api/dbExchange/execddl',
    description: 'Sadece CREATE VIEW / CREATE OR REPLACE VIEW komutlarını çalıştırır.',
    dbTables: 'vw_* görünümleri',
    frontendPages: ['/core/cache'],
  },
  {
    id: 34,
    category: 'PostgreSQL dbExchange',
    name: 'Stream SQL Query',
    methods: ['POST'],
    path: '/api/dbExchange/stream_sql_query',
    description: 'SELECT sorgusunu NDJSON olarak stream eder (statement_timeout kontrollü).',
    dbTables: 'dinamik (sorguya bağlı)',
    frontendPages: ['büyük raporlar / exportlar'],
  },
  {
    id: 35,
    category: 'PostgreSQL dbExchange',
    name: 'CSV/Excel Import',
    methods: ['POST'],
    path: '/api/dbExchange/csv_excel_import',
    description: 'Yüklenen CSV dosyasını belirtilen tabloya import eder.',
    dbTables: 'dinamik (table_name parametresi)',
    frontendPages: ['/core/cache'],
  },

  // SQL Server sqlExchange
  {
    id: 40,
    category: 'SQL Server sqlExchange',
    name: 'Tablo Verisi Getir (MSSQL)',
    methods: ['GET'],
    path: '/api/sqlExchange/get',
    description: 'SQL Server tablosundan veri okur.',
    dbTables: 'dbo.* (table_name parametresi, INFORMATION_SCHEMA.* destekli)',
    frontendPages: ['PPM / DiAPCQS entegrasyonu'],
  },
  {
    id: 41,
    category: 'SQL Server sqlExchange',
    name: 'Raw Query (MSSQL)',
    methods: ['GET', 'POST'],
    path: '/api/sqlExchange/getquery',
    description: 'Raw SQL SELECT sorgularını SQL Server üzerinde çalıştırır.',
    dbTables: 'dinamik',
    frontendPages: ['rapor / entegrasyon ekranları'],
  },
  {
    id: 42,
    category: 'SQL Server sqlExchange',
    name: 'Tarih Aralığı Sorgu (MSSQL)',
    methods: ['GET'],
    path: '/api/sqlExchange/get_between_dates',
    description: 'SQL Server tablosundan tarih aralığına göre veri getirir.',
    dbTables: 'dbo.*',
    frontendPages: ['raporlama'],
  },
  {
    id: 43,
    category: 'SQL Server sqlExchange',
    name: 'Insert (MSSQL)',
    methods: ['POST'],
    path: '/api/sqlExchange/insert',
    description: 'SQL Server tablosuna tekil veya batch insert yapar.',
    dbTables: 'dbo.*',
    frontendPages: ['entegrasyon create'],
  },
  {
    id: 44,
    category: 'SQL Server sqlExchange',
    name: 'Update (MSSQL)',
    methods: ['PUT'],
    path: '/api/sqlExchange/update_data',
    description: 'SQL Server tablosunda belirtilen id alanına göre kaydı günceller.',
    dbTables: 'dbo.*',
    frontendPages: ['entegrasyon edit'],
  },
  {
    id: 45,
    category: 'SQL Server sqlExchange',
    name: 'Delete (MSSQL)',
    methods: ['DELETE'],
    path: '/api/sqlExchange/delete',
    description: 'SQL Server tablosunda field_name/field_value kriterine göre silme yapar.',
    dbTables: 'dbo.*',
    frontendPages: ['entegrasyon silme'],
  },

  // ACC
  {
    id: 50,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'Projeleri Listele',
    methods: ['GET'],
    path: '/api/acc/projects',
    description: 'ACC account içindeki projeleri listeler.',
    dbTables: '- (ACC API)',
    frontendPages: ['/design/acc/projects'],
  },
  {
    id: 51,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'Proje Detayı',
    methods: ['GET'],
    path: '/api/acc/projects/<project_id>',
    description: 'Tek bir ACC projesinin detaylarını getirir.',
    dbTables: '- (ACC API)',
    frontendPages: ['/design/acc/projects/:id'],
  },
  {
    id: 52,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'Proje Klasörleri',
    methods: ['GET'],
    path: '/api/acc/projects/<project_id>/folders',
    description: 'Projenin klasör yapısını listeler (topFolders veya alt klasörler).',
    dbTables: '- (ACC API)',
    frontendPages: ['/design/acc/folders'],
  },
  {
    id: 53,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'Proje Öğeleri (Dosyalar)',
    methods: ['GET'],
    path: '/api/acc/projects/<project_id>/items',
    description: 'Belirli klasördeki ACC dosyalarını listeler.',
    dbTables: '- (ACC API)',
    frontendPages: ['/design/acc/items'],
  },
  {
    id: 54,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'Öğe Detayı',
    methods: ['GET'],
    path: '/api/acc/projects/<project_id>/items/<item_id>',
    description: 'ACC içindeki tek bir öğenin detayını döner.',
    dbTables: '- (ACC API)',
    frontendPages: ['/design/acc/items/:id'],
  },
  {
    id: 55,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'Dosya İndir (PDF inline)',
    methods: ['GET'],
    path: '/api/acc/projects/<project_id>/items/<item_id>/download',
    description: 'ACC dosyasının son versiyonunu stream ederek indirir (PDF için inline).',
    dbTables: '- (ACC API, OSS)',
    frontendPages: ['/design/acc/pdf-viewer'],
  },
  {
    id: 56,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'PDF Export Başlat',
    methods: ['POST'],
    path: '/api/acc/files/export/pdf',
    description: 'Birden fazla ACC dosyasını PDF export job olarak tetikler.',
    dbTables: '- (ACC API)',
    frontendPages: ['/design/acc/bulk-export'],
  },
  {
    id: 57,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'PDF Export Durumu',
    methods: ['GET'],
    path: '/api/acc/files/export/pdf/<export_id>',
    description: 'ACC PDF export job’unun durumunu sorgular.',
    dbTables: '- (ACC API)',
    frontendPages: ['/design/acc/bulk-export'],
  },
  {
    id: 58,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'Hubs',
    methods: ['GET'],
    path: '/api/acc/hubs',
    description: 'ACC hub (account) listesini döner.',
    dbTables: '- (ACC API)',
    frontendPages: ['/design/acc/projects'],
  },
  {
    id: 59,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'Bağlantı Testi',
    methods: ['GET'],
    path: '/api/acc/test',
    description: 'ACC ile bağlantıyı test eder, temel konfigürasyonu doğrular.',
    dbTables: '- (ACC API)',
    frontendPages: ['/design/acc'],
  },
  {
    id: 60,
    category: 'ACC (Autodesk Construction Cloud)',
    name: 'Config Kontrol',
    methods: ['GET'],
    path: '/api/acc/config-check',
    description: 'ACC config durumunu ve eksik parametreleri raporlar.',
    dbTables: '-',
    frontendPages: ['/design/acc'],
  },

  // Dosya Yükleme & PDF Araçları
  {
    id: 70,
    category: 'Dosya Yükleme',
    name: 'Dosya Yükle (Temp)',
    methods: ['POST'],
    path: '/api/file_upload',
    description: 'Kullanıcının temp klasörüne dosya yükler.',
    dbTables: '- (filesystem /uploads/temp)',
    frontendPages: ['/core/folders'],
  },
  {
    id: 71,
    category: 'Dosya Yükleme',
    name: 'Dosya Yeniden Adlandır',
    methods: ['POST'],
    path: '/api/file_rename',
    description: 'Verilen içerik yolunda dosya adı değiştirir.',
    dbTables: '- (filesystem /uploads)',
    frontendPages: ['/core/folders'],
  },
  {
    id: 72,
    category: 'Dosya Yükleme',
    name: 'Dosya Sil',
    methods: ['POST'],
    path: '/api/file_delete',
    description: 'Verilen içerik yolundaki dosyayı siler.',
    dbTables: '- (filesystem /uploads)',
    frontendPages: ['/core/folders'],
  },
  {
    id: 73,
    category: 'Dosya Yükleme',
    name: 'Klasördeki Dosyaları Listele',
    methods: ['GET'],
    path: '/api/list_files/<content_path>',
    description: 'Kullanıcının kendi upload klasöründeki dosyaları listeler.',
    dbTables: '- (filesystem /uploads)',
    frontendPages: ['/core/folders'],
  },
  {
    id: 74,
    category: 'Dosya Yükleme',
    name: 'Upload Edilmiş Dosya Serve',
    methods: ['GET'],
    path: '/api/uploads/<content_path>/<filename>',
    description: 'Uploads klasöründen dosya döner.',
    dbTables: '- (filesystem /uploads)',
    frontendPages: ['/core/folders', '/design/acc/pdf-viewer'],
  },
  {
    id: 75,
    category: 'Dosya Yükleme',
    name: 'Finalize Upload (Remote)',
    methods: ['POST'],
    path: '/api/finalize_upload',
    description: 'Temp’teki dosyaları REMOTE_UPLOAD_URL ile uzak sunucuya taşır.',
    dbTables: '- (filesystem /uploads, remote storage)',
    frontendPages: ['/core/folders'],
  },
  {
    id: 76,
    category: 'Dosya Yükleme',
    name: 'Uploads Alt Klasörlerini Listele',
    methods: ['GET'],
    path: '/api/list_upload_subfolders',
    description: 'Ana uploads klasörü altındaki klasör adlarını listeler.',
    dbTables: '- (filesystem /uploads)',
    frontendPages: ['/core/folders'],
  },
  {
    id: 77,
    category: 'PDF / Form Araçları',
    name: 'PDF Form Parse',
    methods: ['POST'],
    path: '/api/pdf_form_parse',
    description: 'Yüklenen PDF’ten form alanlarını ve statik metinleri JSON formatında çıkarır.',
    dbTables: '-',
    frontendPages: ['/core/general/pdf-to-react'],
  },
  {
    id: 78,
    category: 'PDF / Form Araçları',
    name: 'PDF Font Extract',
    methods: ['POST'],
    path: '/api/extract_fonts',
    description: 'PDF içindeki fontları analiz edip döner.',
    dbTables: '-',
    frontendPages: ['/core/general/pdf-to-react'],
  },
  {
    id: 79,
    category: 'PDF / Form Araçları',
    name: 'DCC OCR',
    methods: ['POST'],
    path: '/api/dcc/ocr',
    description: 'PDF veya görsellerden OCR ile metin çıkarır. file veya file_path ile çalışır.',
    dbTables: '-',
    frontendPages: ['/dcc/ocr'],
  },
  {
    id: 79.1,
    category: 'Dosya Metadata Araçları',
    name: 'DCC EXIF Oku',
    methods: ['POST'],
    path: '/api/dcc/exif',
    description: 'Yüklenen görsel veya DCC sunucu yolu üzerinden EXIF metadata bilgisini okur.',
    dbTables: '-',
    frontendPages: ['/core/general/exif-reader'],
  },

  // Admin
  {
    id: 80,
    category: 'Admin',
    name: 'SQL Server → PostgreSQL Tablo Kopyala',
    methods: ['POST'],
    path: '/api/admin/copy-table',
    description: 'Seçili SQL Server tablosunu PostgreSQL’e kopyalar (opsiyonel mapping/where ile).',
    dbTables: 'kaynak: PPMTRAIN/DiAPCQS, hedef: TKF_ADMIN',
    frontendPages: ['/core/cache'],
  },

  // PostgREST (TKF_ADMIN / public schema) - Otomatik oluşturulan tablo endpointleri (port 3000)
  {
    id: 200,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'building_types',
    methods: ['GET'],
    path: '/building_types',
    description: 'PostgREST tablosu: building_types (bina tipleri referans verisi).',
    dbTables: 'building_types',
    frontendPages: [],
  },
  {
    id: 201,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'building_usage',
    methods: ['GET'],
    path: '/building_usage',
    description: 'PostgREST tablosu: building_usage (bina kullanım tipleri).',
    dbTables: 'building_usage',
    frontendPages: [],
  },
  {
    id: 202,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'buildings',
    methods: ['GET'],
    path: '/buildings',
    description: 'PostgREST tablosu: buildings (bina listesi).',
    dbTables: 'buildings',
    frontendPages: ['/work-permits/create'],
  },
  {
    id: 203,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'companies',
    methods: ['GET'],
    path: '/companies',
    description: 'PostgREST tablosu: companies (şirketler).',
    dbTables: 'to_companies',
    frontendPages: ['/companies', '/work-permits/create'],
  },
  {
    id: 204,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'company_services',
    methods: ['GET'],
    path: '/company_services',
    description: 'PostgREST tablosu: company_services (şirket hizmetleri).',
    dbTables: 'company_services',
    frontendPages: [],
  },
  {
    id: 205,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'contract_payments',
    methods: ['GET'],
    path: '/contract_payments',
    description: 'PostgREST tablosu: contract_payments (sözleşme ödemeleri).',
    dbTables: 'contract_payments',
    frontendPages: [],
  },
  {
    id: 206,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'contracts',
    methods: ['GET'],
    path: '/contracts',
    description: 'PostgREST tablosu: contracts (sözleşmeler).',
    dbTables: 'contracts',
    frontendPages: [],
  },
  {
    id: 207,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'cor_contracts',
    methods: ['GET'],
    path: '/cor_contracts',
    description: 'PostgREST tablosu: cor_contracts (core sözleşme verileri).',
    dbTables: 'cor_contracts',
    frontendPages: [],
  },
  {
    id: 208,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'cor_countries',
    methods: ['GET'],
    path: '/cor_countries',
    description: 'PostgREST tablosu: cor_countries (ülke listesi).',
    dbTables: 'cor_countries',
    frontendPages: ['/core/general/countries/list'],
  },
  {
    id: 209,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'cor_currencies',
    methods: ['GET'],
    path: '/cor_currencies',
    description: 'PostgREST tablosu: cor_currencies (para birimleri).',
    dbTables: 'cor_currencies',
    frontendPages: ['/core/general/currencies/list'],
  },
  {
    id: 210,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'cor_trade_types',
    methods: ['GET'],
    path: '/cor_trade_types',
    description: 'PostgREST tablosu: cor_trade_types (ticaret türleri).',
    dbTables: 'cor_trade_types',
    frontendPages: ['/companies'],
  },
  {
    id: 211,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'counter_master',
    methods: ['GET'],
    path: '/counter_master',
    description: 'PostgREST tablosu: counter_master (sayaç master kayıtları).',
    dbTables: 'counter_master',
    frontendPages: [],
  },
  {
    id: 212,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'counter_readings',
    methods: ['GET'],
    path: '/counter_readings',
    description: 'PostgREST tablosu: counter_readings (sayaç okuma kayıtları).',
    dbTables: 'counter_readings',
    frontendPages: [],
  },
  {
    id: 213,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'counters',
    methods: ['GET'],
    path: '/counters',
    description: 'PostgREST tablosu: counters (sayaçlar).',
    dbTables: 'counters',
    frontendPages: [],
  },
  {
    id: 214,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'emergency_contacts',
    methods: ['GET'],
    path: '/emergency_contacts',
    description: 'PostgREST tablosu: emergency_contacts (acil durum iletişimleri).',
    dbTables: 'emergency_contacts',
    frontendPages: ['/work-permits/create'],
  },
  {
    id: 215,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'internet_devices',
    methods: ['GET'],
    path: '/internet_devices',
    description: 'PostgREST tablosu: internet_devices (internet cihazları).',
    dbTables: 'internet_devices',
    frontendPages: ['/it/envanter'],
  },
  {
    id: 216,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'logs',
    methods: ['GET'],
    path: '/logs',
    description: 'PostgREST tablosu: logs (uygulama logları).',
    dbTables: 'logs',
    frontendPages: ['/it/logs'],
  },
  {
    id: 217,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'personnel',
    methods: ['GET'],
    path: '/personnel',
    description: 'PostgREST tablosu: personnel (personel listesi).',
    dbTables: 'adm_personnel',
    frontendPages: ['/personnel', '/work-permits/create'],
  },
  {
    id: 218,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'personnel_accommodation',
    methods: ['GET'],
    path: '/personnel_accommodation',
    description: 'PostgREST tablosu: personnel_accommodation (lojman/konaklama verileri).',
    dbTables: 'personnel_accommodation',
    frontendPages: [],
  },
  {
    id: 219,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'personnel_services',
    methods: ['GET'],
    path: '/personnel_services',
    description: 'PostgREST tablosu: personnel_services (servis/servis kayıtları).',
    dbTables: 'personnel_services',
    frontendPages: [],
  },
  {
    id: 220,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'rooms',
    methods: ['GET'],
    path: '/rooms',
    description: 'PostgREST tablosu: rooms (oda/alan tanımları).',
    dbTables: 'rooms',
    frontendPages: ['/work-permits/create'],
  },
  {
    id: 221,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'work_permit_approvals',
    methods: ['GET'],
    path: '/work_permit_approvals',
    description: 'PostgREST tablosu: work_permit_approvals (izin onay kayıtları).',
    dbTables: 'work_permit_approvals',
    frontendPages: ['/work-permits'],
  },
  {
    id: 222,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'work_permit_conflicts',
    methods: ['GET'],
    path: '/work_permit_conflicts',
    description: 'PostgREST tablosu: work_permit_conflicts (izin çakışma kayıtları).',
    dbTables: 'work_permit_conflicts',
    frontendPages: ['/work-permits'],
  },
  {
    id: 223,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'work_permit_gas_tests',
    methods: ['GET'],
    path: '/work_permit_gas_tests',
    description: 'PostgREST tablosu: work_permit_gas_tests (gaz testleri).',
    dbTables: 'work_permit_gas_tests',
    frontendPages: ['/work-permits'],
  },
  {
    id: 224,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'work_permit_ppe',
    methods: ['GET'],
    path: '/work_permit_ppe',
    description: 'PostgREST tablosu: work_permit_ppe (KKD kayıtları).',
    dbTables: 'work_permit_ppe',
    frontendPages: ['/work-permits'],
  },
  {
    id: 225,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'work_permit_requirements',
    methods: ['GET'],
    path: '/work_permit_requirements',
    description: 'PostgREST tablosu: work_permit_requirements (izin gereklilikleri).',
    dbTables: 'work_permit_requirements',
    frontendPages: ['/work-permits'],
  },
  {
    id: 226,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'work_permit_types',
    methods: ['GET'],
    path: '/work_permit_types',
    description: 'PostgREST tablosu: work_permit_types (izin türleri).',
    dbTables: 'work_permit_types',
    frontendPages: ['/work-permits/create'],
  },
  {
    id: 227,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'work_permits',
    methods: ['GET'],
    path: '/work_permits',
    description: 'PostgREST tablosu: work_permits (tüm çalışma izinleri).',
    dbTables: 'work_permits',
    frontendPages: ['/work-permits'],
  },
  {
    id: 228,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'certificates',
    methods: ['GET'],
    path: '/certificates',
    description: 'PostgREST tablosu: certificates (sertifikalar).',
    dbTables: 'certificates',
    frontendPages: ['/work-permits'],
  },
  {
    id: 229,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'certificate_types',
    methods: ['GET'],
    path: '/certificate_types',
    description: 'PostgREST tablosu: certificate_types (sertifika türleri).',
    dbTables: 'certificate_types',
    frontendPages: ['/work-permits/create'],
  },
  {
    id: 230,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'permit_certificate_relations',
    methods: ['GET'],
    path: '/permit_certificate_relations',
    description: 'PostgREST tablosu: permit_certificate_relations (izin-sertifika ilişkileri).',
    dbTables: 'permit_certificate_relations',
    frontendPages: ['/work-permits/create'],
  },
  {
    id: 232,
    category: 'PostgREST (TKF_ADMIN/public)',
    name: 'permit_validity_periods',
    methods: ['GET'],
    path: '/permit_validity_periods',
    description: 'PostgREST tablosu: permit_validity_periods (izin/sertifika geçerlilik süreleri).',
    dbTables: 'permit_validity_periods',
    frontendPages: ['/work-permits'],
  },
];

const methodColor: Record<HttpMethod, string> = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  DELETE: 'red',
  PATCH: 'purple',
};

const CoreApiEndpoints: React.FC = () => {
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState<HttpMethod | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);

  const categories = useMemo(
    () => Array.from(new Set(apiEndpoints.map(e => e.category))).sort(),
    []
  );

  const methods = useMemo(
    () => Array.from(new Set(apiEndpoints.flatMap(e => e.methods))),
    []
  );

  const filteredData = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    return apiEndpoints.filter(ep => {
      if (methodFilter && !ep.methods.includes(methodFilter)) {
        return false;
      }
      if (categoryFilter && ep.category !== categoryFilter) {
        return false;
      }
      if (searchLower) {
        const haystack = [
          ep.name,
          ep.path,
          ep.description,
          ep.category,
          ep.dbTables,
          ep.frontendPages.join(' '),
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(searchLower)) {
          return false;
        }
      }
      return true;
    });
  }, [search, methodFilter, categoryFilter]);

  const columns = [
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category',
      width: 160,
    },
    {
      title: 'Ad',
      dataIndex: 'name',
      key: 'name',
      width: 260,
    },
    {
      title: 'Method',
      dataIndex: 'methods',
      key: 'methods',
      width: 90,
      render: (vals: HttpMethod[]) => (
        <span>
          {(vals || []).map(m => (
            <Tag key={m} color={methodColor[m] || 'default'}>
              {m}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      title: 'Endpoint',
      dataIndex: 'path',
      key: 'path',
      width: 260,
      render: (val: string) => (
        <Text code style={{ userSelect: 'text' }}>
          {val}
        </Text>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      width: 320,
    },
    {
      title: 'DB Tablo(lar)',
      dataIndex: 'dbTables',
      key: 'dbTables',
      width: 220,
    },
    {
      title: 'Frontend Sayfaları',
      dataIndex: 'frontendPages',
      key: 'frontendPages',
      width: 260,
      render: (pages: string[]) =>
        pages && pages.length > 0 ? (
          <span>
            {pages.map(p => (
              <Tag key={p}>{p}</Tag>
            ))}
          </span>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <ApiOutlined style={{ marginRight: 8 }} />
            API Endpoint Referansı
          </Title>
          <Text type="secondary">
            Tüm ana backend endpoint’lerinin yolu, HTTP methodu ve kısa açıklaması.
          </Text>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={10} lg={8}>
          <Input
            placeholder="Endpoint, açıklama, tablo veya sayfa içinde ara..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
          />
        </Col>
        <Col xs={12} sm={6} md={7} lg={6}>
          <Select
            allowClear
            placeholder="HTTP Method"
            value={methodFilter}
            onChange={value => setMethodFilter(value as HttpMethod | undefined)}
            style={{ width: '100%' }}
          >
            {methods.map(m => (
              <Option key={m} value={m}>
                {m}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} sm={6} md={7} lg={6}>
          <Select
            allowClear
            placeholder="Kategori"
            value={categoryFilter}
            onChange={value => setCategoryFilter(value)}
            style={{ width: '100%' }}
          >
            {categories.map(cat => (
              <Option key={cat} value={cat}>
                {cat}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <StandardTable
        dataSource={filteredData}
        columns={columns as any}
        rowKey="id"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1200 }}
        sticky
      />
    </Card>
  );
};

export default CoreApiEndpoints;


